/**
 * <vv-price-slider> — dual-range price slider that drives Horizon's native
 * price facet (the two min/max number inputs rendered by price-filter.liquid).
 *
 * The reference design (design-reference / plpSearch.html) shows a track with a
 * fill, two thumbs, a "$min – $max" label, a Reset link and min/max ticks.
 * Horizon ships min/max text inputs instead; this component layers the slider
 * on top, keeps the native inputs in the DOM (visually hidden) so the form still
 * submits the correct `filter.v.price.gte` / `.lte` params, and dispatches a
 * `change` event on those inputs so price-facet-component re-runs the search.
 *
 * No external dependencies (per project conventions: Web Components only).
 */
class VvPriceSlider extends HTMLElement {
  // Horizon re-renders the facets by morphing the DOM. Depending on how the
  // morph keys elements, this host can either be replaced (a fresh element
  // runs connectedCallback) or preserved with its children swapped/reset. To
  // survive both cases, setup is re-entrant: it re-queries children and
  // re-binds every time, and re-runs whenever the morph updates these data
  // attributes on a preserved host.
  static get observedAttributes() {
    return ['data-min', 'data-max', 'data-value-min', 'data-value-max'];
  }

  connectedCallback() {
    this.#setup();
  }

  attributeChangedCallback() {
    if (this.isConnected) this.#setup();
  }

  #setup() {
    this.min = this.#num(this.dataset.min, 0);
    this.max = this.#num(this.dataset.max, 0);
    this.symbol = this.dataset.currencySymbol || '$';

    this.rangeMin = this.querySelector('[data-vv-range="min"]');
    this.rangeMax = this.querySelector('[data-vv-range="max"]');
    this.fill = this.querySelector('[data-vv-fill]');
    this.label = this.querySelector('[data-vv-label]');
    this.resetBtn = this.querySelector('[data-vv-reset]');
    this.tickMin = this.querySelector('[data-vv-tick-min]');
    this.tickMax = this.querySelector('[data-vv-tick-max]');
    this.track = this.querySelector('[data-vv-track]');

    // The native Horizon inputs live in the same price-facet component.
    const facet = this.closest('.price-facet') || this.parentElement;
    this.nativeMin = facet ? facet.querySelector('[ref="minInput"]') : null;
    this.nativeMax = facet ? facet.querySelector('[ref="maxInput"]') : null;

    if (!this.rangeMin || !this.rangeMax || this.max <= this.min) {
      // No usable range (e.g. all products $0) — leave the native inputs visible.
      this.classList.add('vv-price-slider--inert');
      return;
    }
    this.classList.remove('vv-price-slider--inert');

    const step = Math.max(1, Math.round((this.max - this.min) / 100));
    for (const input of [this.rangeMin, this.rangeMax]) {
      input.min = this.min;
      input.max = this.max;
      input.step = step;
    }

    const startMin = this.#num(this.dataset.valueMin, this.min);
    const startMax = this.#num(this.dataset.valueMax, this.max);
    this.rangeMin.value = Math.min(startMin, startMax);
    this.rangeMax.value = Math.max(startMin, startMax);

    // Property handlers (not addEventListener) so re-running setup never
    // stacks duplicate listeners on a preserved node.
    this.rangeMin.oninput = () => this.#onInput('min');
    this.rangeMax.oninput = () => this.#onInput('max');
    this.rangeMin.onchange = () => this.#commit();
    this.rangeMax.onchange = () => this.#commit();
    if (this.resetBtn) {
      this.resetBtn.onclick = () => this.#reset();
    }

    if (this.tickMin) this.tickMin.textContent = this.#money(this.min);
    if (this.tickMax) this.tickMax.textContent = this.#money(this.max);
    this.#render();
  }

  #onInput(which) {
    let lo = Number(this.rangeMin.value);
    let hi = Number(this.rangeMax.value);
    // Prevent the thumbs from crossing.
    if (lo > hi) {
      if (which === 'min') {
        lo = hi;
        this.rangeMin.value = lo;
      } else {
        hi = lo;
        this.rangeMax.value = hi;
      }
    }
    this.#render();
  }

  #render() {
    const lo = Number(this.rangeMin.value);
    const hi = Number(this.rangeMax.value);
    const span = this.max - this.min || 1;
    const loPct = ((lo - this.min) / span) * 100;
    const hiPct = ((hi - this.min) / span) * 100;
    if (this.fill) {
      this.fill.style.left = loPct + '%';
      this.fill.style.right = 100 - hiPct + '%';
    }
    // Drive the visible track as a gradient (light → dark fill → light) so the
    // dark connecting line always shows, independent of thumb/input stacking.
    if (this.track) {
      this.track.style.background =
        'linear-gradient(to right, #e8e8e4 0% ' + loPct + '%, #000 ' + loPct + '% ' + hiPct + '%, #e8e8e4 ' + hiPct + '% 100%)';
    }
    if (this.label) {
      this.label.textContent = `${this.#money(lo)} – ${this.#money(hi)}`;
    }
  }

  #commit() {
    const lo = Number(this.rangeMin.value);
    const hi = Number(this.rangeMax.value);
    this.#setNative(this.nativeMin, lo > this.min ? lo : '');
    this.#setNative(this.nativeMax, hi < this.max ? hi : '');
  }

  #reset() {
    this.rangeMin.value = this.min;
    this.rangeMax.value = this.max;
    this.#render();
    this.#setNative(this.nativeMin, '');
    this.#setNative(this.nativeMax, '');
  }

  #setNative(input, value) {
    if (!input) return;
    input.value = value === '' ? '' : String(value);
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  #money(value) {
    const rounded = Math.round(Number(value));
    return this.symbol + rounded.toLocaleString();
  }

  #num(value, fallback) {
    const n = parseFloat(String(value).replace(/[^0-9.]/g, ''));
    return Number.isFinite(n) ? n : fallback;
  }
}

if (!customElements.get('vv-price-slider')) {
  customElements.define('vv-price-slider', VvPriceSlider);
}
